import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Building2, Users2, LayoutGrid } from 'lucide-react';
import { DataTable, Column } from '@/components/common/DataTable';
import { Pagination } from '@/components/common/Pagination';
import { SearchBar } from '@/components/common/SearchBar';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/common/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/common/Dialog';
import { useToast } from '@/components/common/Toast';
import * as accountsService from '@/services/api/accountsService';
import type { Account } from '@/services/api/accountsService';
import { usePermissions } from '@/hooks/usePermissions';

// Premium color mappings with gradients and animations
const statusColors = {
  ACTIVE: 'active',           // Emerald to Green gradient - Vibrant, healthy
  INACTIVE: 'inactive',       // Slate gray - Subtle, dormant
  MQL_READY: 'mqlReady',     // Rose to Pink gradient with pulse - Urgent attention!
  MQL_SUBMITTED: 'mqlSubmitted', // Cyan to Blue gradient - In process
} as const;

const tierColors = {
  1: 'tier1',     // Amber to Orange gradient - Premium gold, highest priority
  2: 'tier2',     // Violet to Purple gradient - Royal, medium priority
  3: 'tier3',     // Slate gradient - Professional gray, lower priority
} as const;

export default function AccountsPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { canCreate, canEdit, canDelete } = usePermissions('accounts');
  
  // State
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'BUYER' | 'PARTNER'>('ALL');
  const [counts, setCounts] = useState<{ total: number; buyer: number; partner: number }>({ total: 0, buyer: 0, partner: 0 });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  // Fetch accounts from backend
  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await accountsService.getAccounts({
        search: searchQuery || undefined,
        accountType: typeFilter === 'ALL' ? undefined : typeFilter,
        page: currentPage,
        limit: pageSize,
      });
      
      setAccounts(response.data);
      setTotalItems(response.total);
      
      // Save to localStorage for ABM Journey integration
      localStorage.setItem('accounts', JSON.stringify(response.data));
      console.log('✅ Saved accounts to localStorage:', response.data.length);
    } catch (error: any) {
      console.error('Error fetching accounts:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: error.response?.data?.error || 'Failed to load accounts',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch buyer/partner counts for the summary cards (independent of the active tab).
  const fetchCounts = async () => {
    try {
      const [all, buyers, partners] = await Promise.all([
        accountsService.getAccounts({ search: searchQuery || undefined, page: 1, limit: 1 }),
        accountsService.getAccounts({ search: searchQuery || undefined, accountType: 'BUYER', page: 1, limit: 1 }),
        accountsService.getAccounts({ search: searchQuery || undefined, accountType: 'PARTNER', page: 1, limit: 1 }),
      ]);
      setCounts({ total: all.total, buyer: buyers.total, partner: partners.total });
    } catch { /* non-fatal — cards just show stale/zero */ }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchAccounts();
  }, [searchQuery, currentPage, pageSize, typeFilter]);

  useEffect(() => {
    fetchCounts();
  }, [searchQuery]);

  // Handle delete
  const handleDelete = async () => {
    if (!selectedAccount) return;

    try {
      await accountsService.deleteAccount(selectedAccount.id);
      
      addToast({
        type: 'success',
        title: 'Account Deleted',
        description: `${selectedAccount.name} has been deleted successfully.`,
      });
      
      setDeleteDialogOpen(false);
      setSelectedAccount(null);
      fetchAccounts(); // Refresh list
    } catch (error: any) {
      console.error('Error deleting account:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete account',
      });
    }
  };

  // Column definitions
  const columns: Column<Account>[] = [
    {
      key: 'name',
      header: 'Account Name',
      accessor: 'name',
      sortable: true,
      width: '22%',
      render: (value) => (
        <div className="font-medium">{value}</div>
      ),
    },
    {
      key: 'accountType',
      header: 'Type',
      accessor: 'accountType',
      sortable: true,
      width: '11%',
      render: (value, account) => {
        const partner = String(value || 'BUYER').toUpperCase() === 'PARTNER';
        return (
          <div className="flex flex-col gap-0.5">
            <Badge variant={partner ? 'mqlReady' : 'tier1'} className="w-fit">
              {partner ? 'Partner' : 'Buyer'}
            </Badge>
            {partner && account.partnerType && (
              <span className="text-[10px] text-muted-foreground">{String(account.partnerType).replace(/_/g, ' ')}</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'industryVertical',
      header: 'Industry',
      accessor: 'industryVertical',
      sortable: true,
      width: '15%',
      render: (value) => value || 'N/A',
    },
    {
      key: 'abmTier',
      header: 'Tier',
      accessor: 'abmTier',
      sortable: true,
      width: '10%',
      render: (value) => value ? (
        <Badge variant={tierColors[value as keyof typeof tierColors]}>
          Tier {value}
        </Badge>
      ) : 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      width: '10%',
      render: (value: Account['status']) => {
        // Format status text for display
        const statusText = value.replace('_', ' ');
        return (
          <Badge variant={statusColors[value]}>
            {statusText}
          </Badge>
        );
      },
    },
    {
      key: 'region',
      header: 'Region',
      accessor: 'region',
      sortable: true,
      width: '12%',
      render: (value) => value || 'N/A',
    },
    {
      key: 'maturityBand',
      header: 'Maturity',
      accessor: 'maturityBand',
      sortable: true,
      width: '12%',
      render: (value) => value || 'N/A',
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: 'id',
      width: '13%',
      align: 'center',
      render: (_, account) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/accounts/${account.id}`);
            }}
            title="View details"
          >
            <Eye className="h-4 w-4" />
          </Button>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/accounts/${account.id}/edit`);
              }}
              title="Edit account"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAccount(account);
                setDeleteDialogOpen(true);
              }}
              title="Delete account"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Accounts"
        description="Manage your target accounts and track engagement"
        actions={
          canCreate ? (
            <Button onClick={() => navigate('/accounts/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Add Account
            </Button>
          ) : undefined
        }
      />

      {/* Buyer vs Partner summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card
          className={`p-4 cursor-pointer transition-all ${typeFilter === 'ALL' ? 'ring-2 ring-primary' : 'hover:bg-muted/30'}`}
          onClick={() => { setTypeFilter('ALL'); setCurrentPage(1); }}
        >
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <LayoutGrid className="h-4 w-4" /> All Accounts
          </div>
          <div className="text-2xl font-bold">{counts.total}</div>
        </Card>
        <Card
          className={`p-4 cursor-pointer transition-all ${typeFilter === 'BUYER' ? 'ring-2 ring-primary' : 'hover:bg-muted/30'}`}
          onClick={() => { setTypeFilter('BUYER'); setCurrentPage(1); }}
        >
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <Building2 className="h-4 w-4" /> Buyers <span className="text-[10px] text-muted-foreground">(→ MQL)</span>
          </div>
          <div className="text-2xl font-bold">{counts.buyer}</div>
        </Card>
        <Card
          className={`p-4 cursor-pointer transition-all ${typeFilter === 'PARTNER' ? 'ring-2 ring-primary' : 'hover:bg-muted/30'}`}
          onClick={() => { setTypeFilter('PARTNER'); setCurrentPage(1); }}
        >
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <Users2 className="h-4 w-4" /> Partners <span className="text-[10px] text-muted-foreground">(→ PQL)</span>
          </div>
          <div className="text-2xl font-bold">{counts.partner}</div>
        </Card>
      </div>

      {/* Type tabs */}
      <div className="flex gap-1 border-b border-border">
        {(['ALL', 'BUYER', 'PARTNER'] as const).map((t) => (
          <button
            key={t}
            onClick={() => { setTypeFilter(t); setCurrentPage(1); }}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              typeFilter === t
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'ALL' ? 'All' : t === 'BUYER' ? 'Buyers' : 'Partners'}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <SearchBar
        value={searchQuery}
        onChange={(value) => {
          setSearchQuery(value);
          setCurrentPage(1); // Reset to first page on search
        }}
        placeholder={`Search ${typeFilter === 'PARTNER' ? 'partners' : typeFilter === 'BUYER' ? 'buyers' : 'accounts'} by name, industry...`}
      />

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-muted-foreground">Loading accounts...</p>
        </div>
      )}

      {/* Data Table */}
      {!loading && (
        <>
          <DataTable
            data={accounts}
            columns={columns}
            onRowClick={(account) => {
              navigate(`/accounts/${account.id}`);
            }}
          />

          {/* Empty State */}
          {accounts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg font-medium">No accounts found</p>
              <p className="text-muted-foreground mt-1">
                {searchQuery 
                  ? 'Try adjusting your search terms'
                  : 'Get started by creating your first account'
                }
              </p>
              {!searchQuery && canCreate && (
                <Button className="mt-4" onClick={() => navigate('/accounts/new')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Account
                </Button>
              )}
            </div>
          )}

          {/* Pagination */}
          {accounts.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogClose onClose={() => setDeleteDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-semibold">{selectedAccount?.name}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
